import React from "react";
import { Flame, MapPin, Instagram, ShoppingBag, Plus, Lock } from "lucide-react";
import { MENU_ITEMS, SPECIAL_ITEMS, DESSERT_ITEM, TAVERN_ITEMS, FISH_ITEM, BOMBS_ITEM, HERO_IMAGE, SMOKE_TEXTURE } from "../data/menu";
import { useCart } from "../context/CartContext";
import { useKitchenStatus } from "../hooks/useKitchenStatus";
import { useMenuAvailability } from "../hooks/useMenuAvailability";
import { usePromos } from "../hooks/usePromos";

const WING_IDS = [
  "garlic_parmesan_wings",
  "bbq_wings",
  "buffalo_wings",
  "chipotle_firecracker_wings",
];

const SUNDAY_IDS = [
  "betty_cracker_chicken_bowl",
  "pork_crack_mash_bowl",
  "pulled_pork_sandwich",
  "loaded_pork_fries",
  "bbq_bavarian_pretzel",
  "garlic_parmesan_wings",
  "bbq_wings",
  "buffalo_wings",
  "mini_pulled_pork_sandwich",
  "order_of_fries",
];

const SWEET_IDS = ["dessert_pizza"];

const BAVARIAN_STRIPE =
  "repeating-linear-gradient(45deg, #2C6FB5 0px, #2C6FB5 14px, #fff 14px, #fff 28px)";

const Navbar = () => {
  const cart = useCart();
  return (
    <nav
      data-testid="main-nav"
      className="fixed top-0 left-0 right-0 z-50 bg-black/60 backdrop-blur-xl border-b border-white/10"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
        <a href="#top" data-testid="brand-logo" className="flex items-center gap-2 group">
          <Flame className="w-5 h-5 text-[#FF3B30] group-hover:rotate-12 transition-transform" />
          <span className="font-['Anton'] text-xl tracking-widest uppercase text-white">
            Smokey <span className="text-[#FF3B30]">AF</span>
          </span>
        </a>
        <div className="hidden md:flex items-center gap-10 font-['Outfit'] text-sm uppercase tracking-[0.2em] text-zinc-400">
          <a href="#menu" data-testid="nav-menu" className="hover:text-white transition-colors">
            Menu
          </a>
          <a href="#story" data-testid="nav-story" className="hover:text-white transition-colors">
            The Story
          </a>
          <a href="#visit" data-testid="nav-visit" className="hover:text-white transition-colors">
            Visit
          </a>
        </div>
        <button
          onClick={cart.open}
          data-testid="nav-cart-btn"
          className="relative inline-flex items-center gap-2 font-['Anton'] text-sm tracking-widest uppercase bg-[#FF3B30] text-white px-5 py-2 hover:bg-white hover:text-black transition-colors"
        >
          <ShoppingBag className="w-4 h-4" />
          <span className="hidden sm:inline">Order</span>
          {cart.count > 0 && (
            <span
              data-testid="nav-cart-count"
              className="bg-black text-white border border-white/30 group-hover:border-black font-['Anton'] text-[11px] tracking-wider min-w-[20px] h-5 px-1 inline-flex items-center justify-center rounded-full"
            >
              {cart.count}
            </span>
          )}
        </button>
      </div>
    </nav>
  );
};

const SpecialsBanner = ({ specials }) => {
  if (!specials.length) return null;
  return (
    <div data-testid="specials-banner" className="fixed top-16 left-0 right-0 z-40">
      {specials.map((s) => (
        <button
          key={s.id}
          onClick={() =>
            document
              .getElementById(`menu-card-${s.id}`)
              ?.scrollIntoView({ behavior: "smooth", block: "center" })
          }
          data-testid={`specials-banner-${s.id}`}
          className="w-full bg-[#FF3B30] hover:bg-[#d92c22] text-white py-2.5 px-4 flex items-center justify-center gap-3 border-b border-black/30 transition-colors"
        >
          <Flame className="w-4 h-4 animate-pulse flex-shrink-0" />
          <span className="font-['Anton'] tracking-widest uppercase text-sm md:text-base leading-none">
            {s.name} · ${s.price} — Live Now
          </span>
          <span className="font-['Outfit'] text-[10px] uppercase tracking-[0.3em] underline underline-offset-4 hidden sm:inline">
            Tap to order
          </span>
        </button>
      ))}
    </div>
  );
};

const Hero = () => (
  <section
    id="top"
    data-testid="hero-section"
    className="relative min-h-screen w-full flex items-end overflow-hidden"
    style={{
      backgroundImage: `url(${HERO_IMAGE})`,
      backgroundSize: "cover",
      backgroundPosition: "center",
    }}
  >
    {/* Overlays */}
    <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/40 to-black" />
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_transparent_0%,_rgba(0,0,0,0.7)_100%)]" />

    <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pb-24 md:pb-40 pt-32 w-full">
      <div className="flex items-center gap-3 mb-8">
        <span className="h-px w-12 bg-[#FF3B30]" />
        <span className="font-['Outfit'] text-xs uppercase tracking-[0.4em] text-[#FF8C00]">
          Low. Slow. Smokey AF.
        </span>
      </div>

      <h1
        data-testid="hero-title"
        className="font-['Anton'] text-6xl sm:text-7xl md:text-9xl tracking-tighter uppercase leading-[0.85] text-white"
      >
        Smokey
        <br />
        <span className="text-[#FF3B30]">AF</span> Barbecue
      </h1>

      <p className="mt-8 max-w-xl font-['Outfit'] text-base md:text-lg text-zinc-300 leading-relaxed">
        Hand-pulled pork, oven-fired pizza, smoked beef and fries built like
        small monuments. {MENU_ITEMS.length} dishes. No filler. Just fire and flavor.
      </p>

      <div className="mt-10 flex flex-wrap gap-4">
        <a
          href="#menu"
          data-testid="hero-cta-menu"
          className="inline-flex items-center font-['Anton'] tracking-widest uppercase bg-[#FF3B30] text-white px-7 py-4 text-sm hover:bg-white hover:text-black transition-colors"
        >
          See the Menu
        </a>
        <a
          href="#visit"
          data-testid="hero-cta-visit"
          className="inline-flex items-center font-['Anton'] tracking-widest uppercase border border-white/30 text-white px-7 py-4 text-sm hover:border-white transition-colors"
        >
          Find Us
        </a>
      </div>

      {/* Bottom stats */}
      <div className="mt-20 grid grid-cols-3 gap-4 md:gap-12 max-w-2xl border-t border-white/10 pt-8">
        <div>
          <div className="font-['Anton'] text-3xl md:text-5xl text-[#FF8C00]">14h</div>
          <div className="font-['Outfit'] text-xs uppercase tracking-widest text-zinc-500 mt-1">
            Smoke Time
          </div>
        </div>
        <div>
          <div className="font-['Anton'] text-3xl md:text-5xl text-[#FF8C00]">{MENU_ITEMS.length}</div>
          <div className="font-['Outfit'] text-xs uppercase tracking-widest text-zinc-500 mt-1">
            Menu Items
          </div>
        </div>
        <div>
          <div className="font-['Anton'] text-3xl md:text-5xl text-[#FF8C00]">$16</div>
          <div className="font-['Outfit'] text-xs uppercase tracking-widest text-zinc-500 mt-1">
            Wood-Fired Pizza
          </div>
        </div>
      </div>
    </div>

    {/* Scroll hint */}
    <div className="absolute bottom-6 right-6 z-10 hidden md:flex flex-col items-end font-['Outfit'] text-[10px] uppercase tracking-[0.4em] text-zinc-500">
      <span>scroll</span>
      <span className="h-10 w-px bg-zinc-700 mt-2" />
    </div>
  </section>
);

const MenuCard = ({ item, className = "", featured = false, idx = 0, isOpen = true, soldOut = false }) => {
  const cart = useCart();
  const disabled = !isOpen || soldOut;
  const multiDip = !!item.dips?.multi;
  const [dip, setDip] = React.useState(
    multiDip ? null : item.dips?.default || null
  );
  const [dipIds, setDipIds] = React.useState(
    multiDip && item.dips?.default ? [item.dips.default] : []
  );
  const [selectedAddonIds, setSelectedAddonIds] = React.useState([]);

  const toggleDip = (id) =>
    setDipIds((prev) =>
      prev.includes(id) ? prev.filter((d) => d !== id) : [...prev, id]
    );

  const toggleAddon = (id) =>
    setSelectedAddonIds((prev) =>
      prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id]
    );

  const selectedAddons = (item.addons || []).filter((a) =>
    selectedAddonIds.includes(a.id)
  );
  const unitPrice = item.price + selectedAddons.reduce((s, a) => s + a.price, 0);

  const handleAdd = () => {
    if (disabled) return;
    let dipId = null;
    let dipName = null;
    if (item.dips) {
      if (multiDip) {
        const chosen = item.dips.choices.filter((c) => dipIds.includes(c.id));
        dipId = chosen.length
          ? chosen.map((c) => c.id).sort().join("+")
          : "none";
        dipName = chosen.length
          ? chosen.map((c) => c.name).join(" + ")
          : "Plain";
      } else {
        const dipChoice = item.dips.choices.find((c) => c.id === dip);
        dipId = dipChoice?.id || null;
        dipName = dipChoice?.name || null;
      }
    }
    cart.add({
      ...item,
      dip: dipId,
      dipName,
      selectedAddons,
    });
  };
  return (
    <article
      id={`menu-card-${item.id}`}
      data-testid={`menu-card-${item.id}`}
      className={`group relative overflow-hidden bg-[#171717] border border-white/5 hover:border-[#FF3B30]/60 hover:shadow-[0_10px_60px_-15px_rgba(255,59,48,0.35)] transition-all duration-500 flex flex-col ${
        soldOut ? "opacity-60" : ""
      } ${className}`}
      style={{ animationDelay: `${idx * 80}ms` }}
    >
    <div
      className={`relative overflow-hidden ${
        featured ? "aspect-[16/12] md:aspect-[16/10]" : "aspect-[4/3]"
      }`}
    >
      <img
        src={item.image}
        alt={item.name}
        loading="lazy"
        className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-110"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />

      {/* Price stamp */}
      <div
        data-testid={`menu-price-${item.id}`}
        className="absolute top-4 right-4 bg-[#FF3B30] text-white font-['Anton'] text-xl md:text-2xl tracking-wider px-3 py-1 rotate-3 group-hover:rotate-0 transition-transform"
      >
        {item.originalPrice && (
          <span className="line-through opacity-60 text-sm md:text-base mr-1.5">
            ${item.originalPrice}
          </span>
        )}
        ${item.price}
      </div>

      {/* Tags */}
      <div className="absolute top-4 left-4 flex flex-wrap gap-2">
        {soldOut && (
          <span
            data-testid={`sold-out-${item.id}`}
            className="bg-[#FF3B30] text-white font-['Anton'] text-[11px] uppercase tracking-[0.25em] px-2 py-1"
          >
            Sold Out
          </span>
        )}
        {item.tags.map((t) => (
          <span
            key={t}
            className="border border-white/30 backdrop-blur-sm bg-black/30 text-white font-['Outfit'] text-[10px] uppercase tracking-[0.2em] px-2 py-1"
          >
            {t}
          </span>
        ))}
      </div>
    </div>

    <div className={`p-6 md:p-8 flex flex-col flex-1`}>
      <h3
        data-testid={`menu-name-${item.id}`}
        className={`font-['Anton'] uppercase tracking-tight text-white leading-none ${
          featured ? "text-4xl md:text-5xl" : "text-2xl md:text-3xl"
        }`}
      >
        {item.name}
      </h3>
      <p
        className={`mt-3 font-['Outfit'] italic text-[#FF8C00] ${
          featured ? "text-base md:text-lg" : "text-sm"
        }`}
      >
        {item.tagline}
      </p>

      <ul
        className={`mt-5 grid ${
          featured ? "grid-cols-2" : "grid-cols-1"
        } gap-x-4 gap-y-1.5 font-['Outfit'] text-sm text-zinc-400`}
      >
        {item.ingredients.map((ing) => (
          <li key={ing} className="flex items-start gap-2 leading-snug">
            <span className="text-[#FF3B30] mt-1.5 flex-shrink-0">▸</span>
            <span>{ing}</span>
          </li>
        ))}
      </ul>

      {item.dips && (
        <div className="mt-5">
          <div className="font-['Outfit'] text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-2">
            {item.dips.label}
          </div>
          <div className="flex flex-wrap gap-2">
            {item.dips.choices.map((c) => {
              const on = multiDip ? dipIds.includes(c.id) : dip === c.id;
              return (
                <button
                  key={c.id}
                  onClick={() => (multiDip ? toggleDip(c.id) : setDip(c.id))}
                  data-testid={`dip-${item.id}-${c.id}`}
                  className={`px-3 py-1.5 font-['Outfit'] text-xs uppercase tracking-widest border transition-colors ${
                    on
                      ? "bg-[#FF3B30] border-[#FF3B30] text-white"
                      : "border-white/20 text-zinc-400 hover:border-white/60 hover:text-white"
                  }`}
                >
                  {c.name}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {item.addons && (
        <div className="mt-5 flex flex-wrap gap-2">
          {item.addons.map((a) => {
            const on = selectedAddonIds.includes(a.id);
            return (
              <button
                key={a.id}
                onClick={() => toggleAddon(a.id)}
                data-testid={`addon-${item.id}-${a.id}`}
                className={`inline-flex items-center gap-2 px-3 py-1.5 font-['Outfit'] text-xs uppercase tracking-widest border transition-colors ${
                  on
                    ? "bg-[#FF8C00] border-[#FF8C00] text-black font-semibold"
                    : "border-white/20 text-zinc-400 hover:border-white/60 hover:text-white"
                }`}
              >
                <Plus className={`w-3 h-3 ${on ? "rotate-45" : ""} transition-transform`} />
                {a.name} · ${a.price}
              </button>
            );
          })}
        </div>
      )}

      <button
        onClick={handleAdd}
        disabled={disabled}
        data-testid={`menu-add-${item.id}`}
        className="mt-6 md:mt-8 inline-flex items-center justify-center gap-2 self-start bg-white text-black font-['Anton'] text-sm tracking-widest uppercase px-5 py-3 hover:bg-[#FF3B30] hover:text-white transition-colors disabled:bg-zinc-700 disabled:text-zinc-400 disabled:cursor-not-allowed disabled:hover:bg-zinc-700 disabled:hover:text-zinc-400"
      >
        {!isOpen ? (
          <>
            <Lock className="w-4 h-4" />
            Kitchen Closed
          </>
        ) : soldOut ? (
          <>
            <Lock className="w-4 h-4" />
            Sold Out
          </>
        ) : (
          <>
            <Plus className="w-4 h-4" />
            Add to Order · ${unitPrice}
          </>
        )}
      </button>
    </div>
  </article>
  );
};

const TOP_ID = "loaded_pork_fries";
const TOP_ITEM = MENU_ITEMS.find((i) => i.id === TOP_ID);

const BombsBanner = ({ isOpen, soldOut }) => {
  const cart = useCart();
  const item = BOMBS_ITEM;
  const disabled = !isOpen || soldOut;
  return (
    <div
      data-testid="bombs-banner"
      className="relative mb-6 md:mb-8 overflow-hidden border-2 border-[#FF8C00] flex flex-col md:flex-row items-stretch"
      style={{
        background: "linear-gradient(100deg, #1a0e02 0%, #000 60%, #140a01 100%)",
        boxShadow: "0 0 60px -15px rgba(255,140,0,0.5)",
      }}
    >
      <div className="relative w-full md:w-80 flex-shrink-0">
        <img
          src={item.image}
          alt={item.name}
          className="w-full h-56 md:h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-transparent to-black/60 hidden md:block" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent md:hidden" />
      </div>
      <div className="flex-1 p-6 md:p-8 flex flex-col justify-center relative">
        <div className="flex flex-wrap items-center gap-2">
          <span className="bg-[#FF8C00] text-black font-['Anton'] text-[11px] uppercase tracking-[0.25em] px-2.5 py-1 animate-pulse">
            New
          </span>
          <span className="border border-[#FF8C00]/60 text-[#FF8C00] font-['Outfit'] text-[10px] uppercase tracking-[0.25em] px-2.5 py-1">
            Chicken & Waffles… But Better
          </span>
        </div>
        <h3 className="font-['Anton'] uppercase tracking-tight text-white leading-[0.9] text-4xl md:text-5xl mt-4">
          Flapjack <span className="text-[#FF8C00]">Chicken</span> Bombs
        </h3>
        <p className="font-['Outfit'] italic text-[#FF8C00] mt-2 text-sm md:text-base">
          {item.tagline}
        </p>
        <p className="font-['Outfit'] text-zinc-400 text-xs md:text-sm mt-2">
          Two chicken tenders dunked in pancake batter and fried into golden
          bombs the size of a deck of cards · powdered sugar · warm syrup for
          dipping — everything you love about chicken & waffles, but better
        </p>
        <div className="mt-5 flex flex-wrap items-center gap-4">
          <span className="font-['Anton'] text-white text-3xl md:text-4xl tracking-wider">
            <span className="text-[#FF8C00]">${item.price}</span>
          </span>
          <button
            onClick={() => !disabled && cart.add(item)}
            disabled={disabled}
            data-testid="bombs-add-btn"
            className="inline-flex items-center gap-2 bg-[#FF8C00] text-black font-['Anton'] text-sm tracking-widest uppercase px-6 py-3 hover:bg-white transition-colors disabled:bg-zinc-700 disabled:text-zinc-400 disabled:cursor-not-allowed"
          >
            {!isOpen ? (
              <>
                <Lock className="w-4 h-4" /> Kitchen Closed
              </>
            ) : soldOut ? (
              <>
                <Lock className="w-4 h-4" /> Sold Out
              </>
            ) : (
              <>
                <Plus className="w-4 h-4" /> Add to Order · ${item.price}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const FishBanner = ({ isOpen, soldOut }) => {
  const cart = useCart();
  const item = FISH_ITEM;
  const disabled = !isOpen || soldOut;
  return (
    <div
      data-testid="fish-banner"
      className="relative mb-6 md:mb-8 overflow-hidden border-2 border-[#2CC9E8] flex flex-col md:flex-row items-stretch"
      style={{
        background: "linear-gradient(100deg, #021a1f 0%, #000 60%, #01141a 100%)",
        boxShadow: "0 0 60px -15px rgba(44,201,232,0.5)",
      }}
    >
      <div className="relative w-full md:w-80 flex-shrink-0">
        <img
          src={item.image}
          alt={item.name}
          className="w-full h-56 md:h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-transparent to-black/60 hidden md:block" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent md:hidden" />
      </div>
      <div className="flex-1 p-6 md:p-8 flex flex-col justify-center relative">
        <div className="flex flex-wrap items-center gap-2">
          <span className="bg-[#2CC9E8] text-black font-['Anton'] text-[11px] uppercase tracking-[0.25em] px-2.5 py-1">
            House Special
          </span>
          <span className="border border-[#2CC9E8]/60 text-[#2CC9E8] font-['Outfit'] text-[10px] uppercase tracking-[0.25em] px-2.5 py-1">
            Fries & Slaw Included
          </span>
        </div>
        <h3 className="font-['Anton'] uppercase tracking-tight text-white leading-[0.9] text-4xl md:text-5xl mt-4">
          Fried <span className="text-[#2CC9E8]">Fish</span> Plate
        </h3>
        <p className="font-['Outfit'] italic text-[#2CC9E8] mt-2 text-sm md:text-base">
          {item.tagline}
        </p>
        <p className="font-['Outfit'] text-zinc-400 text-xs md:text-sm mt-2">
          Beautifully breaded fried fish · straight-cut fries · creamy coleslaw
          · lemon wedge
        </p>
        <div className="mt-5 flex flex-wrap items-center gap-4">
          <span className="font-['Anton'] text-white text-3xl md:text-4xl tracking-wider">
            <span className="text-[#2CC9E8]">${item.price}</span>
          </span>
          <button
            onClick={() => !disabled && cart.add(item)}
            disabled={disabled}
            data-testid="fish-add-btn"
            className="inline-flex items-center gap-2 bg-[#2CC9E8] text-black font-['Anton'] text-sm tracking-widest uppercase px-6 py-3 hover:bg-white transition-colors disabled:bg-zinc-700 disabled:text-zinc-400 disabled:cursor-not-allowed"
          >
            {!isOpen ? (
              <>
                <Lock className="w-4 h-4" /> Kitchen Closed
              </>
            ) : soldOut ? (
              <>
                <Lock className="w-4 h-4" /> Sold Out
              </>
            ) : (
              <>
                <Plus className="w-4 h-4" /> Add to Order · ${item.price}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const FriesBanner = ({ isOpen, soldOut }) => {
  const cart = useCart();
  const item = TOP_ITEM;
  const disabled = !isOpen || soldOut;
  const [jalapenos, setJalapenos] = React.useState(false);
  const addon = item.addons?.[0];
  const price = item.price + (jalapenos && addon ? addon.price : 0);
  const handleAdd = () => {
    if (disabled) return;
    cart.add({ ...item, selectedAddons: jalapenos && addon ? [addon] : [] });
  };
  return (
    <div
      data-testid="fries-banner"
      className="relative mb-6 md:mb-8 overflow-hidden border-2 border-[#F6B93B] flex flex-col md:flex-row items-stretch"
      style={{
        background: "linear-gradient(100deg, #1a1402 0%, #000 60%, #120d01 100%)",
        boxShadow: "0 0 60px -15px rgba(246,185,59,0.5)",
      }}
    >
      <div className="relative w-full md:w-80 flex-shrink-0">
        <img
          src={item.image}
          alt={item.name}
          className="w-full h-56 md:h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-transparent to-black/60 hidden md:block" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent md:hidden" />
      </div>
      <div className="flex-1 p-6 md:p-8 flex flex-col justify-center relative">
        <div className="flex flex-wrap items-center gap-2">
          <span className="bg-[#F6B93B] text-black font-['Anton'] text-[11px] uppercase tracking-[0.25em] px-2.5 py-1 animate-pulse">
            Featured
          </span>
          <span className="border border-[#F6B93B]/60 text-[#F6B93B] font-['Outfit'] text-[10px] uppercase tracking-[0.25em] px-2.5 py-1">
            Fan Favorite
          </span>
        </div>
        <h3 className="font-['Anton'] uppercase tracking-tight text-white leading-[0.9] text-4xl md:text-5xl mt-4">
          Loaded <span className="text-[#F6B93B]">Pork</span> Fries
        </h3>
        <p className="font-['Outfit'] italic text-[#F6B93B] mt-2 text-sm md:text-base">
          {item.tagline}
        </p>
        <p className="font-['Outfit'] text-zinc-400 text-xs md:text-sm mt-2">
          Straight-cut fries · hot fresh pulled pork · hot nacho cheese · BBQ
          drizzle · crispy bacon bits · green onions
        </p>
        {addon && (
          <div className="mt-4">
            <button
              onClick={() => setJalapenos((v) => !v)}
              data-testid="fries-addon-jalapenos"
              className={`inline-flex items-center gap-2 px-3 py-1.5 font-['Outfit'] text-xs uppercase tracking-widest border transition-colors ${
                jalapenos
                  ? "bg-[#FF8C00] border-[#FF8C00] text-black font-semibold"
                  : "border-white/20 text-zinc-400 hover:border-white/60 hover:text-white"
              }`}
            >
              <Plus
                className={`w-3 h-3 ${jalapenos ? "rotate-45" : ""} transition-transform`}
              />
              {addon.name} · ${addon.price}
            </button>
          </div>
        )}
        <div className="mt-5 flex flex-wrap items-center gap-4">
          <span className="font-['Anton'] text-white text-3xl md:text-4xl tracking-wider">
            <span className="text-[#F6B93B]">${price}</span>
          </span>
          <button
            onClick={handleAdd}
            disabled={disabled}
            data-testid="fries-add-btn"
            className="inline-flex items-center gap-2 bg-[#F6B93B] text-black font-['Anton'] text-sm tracking-widest uppercase px-6 py-3 hover:bg-white transition-colors disabled:bg-zinc-700 disabled:text-zinc-400 disabled:cursor-not-allowed"
          >
            {!isOpen ? (
              <>
                <Lock className="w-4 h-4" /> Kitchen Closed
              </>
            ) : soldOut ? (
              <>
                <Lock className="w-4 h-4" /> Sold Out
              </>
            ) : (
              <>
                <Plus className="w-4 h-4" /> Add to Order · ${price}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const TavernBanner = ({ isOpen, soldOut }) => {
  const cart = useCart();
  return (
    <div
      data-testid="tavern-banner"
      className="relative mb-6 md:mb-8 overflow-hidden border-2 border-[#FF3B30] flex flex-col md:flex-row items-stretch"
      style={{
        background: "linear-gradient(100deg, #190404 0%, #000 60%, #120202 100%)",
        boxShadow: "0 0 60px -15px rgba(255,59,48,0.5)",
      }}
    >
      <div className="relative w-full md:w-80 flex-shrink-0">
        <img
          src={TAVERN_ITEMS[2].image}
          alt="Tavern cracker-crust pizzas"
          className="w-full h-56 md:h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-transparent to-black/60 hidden md:block" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent md:hidden" />
      </div>
      <div className="flex-1 p-6 md:p-8 flex flex-col justify-center relative">
        <div className="flex flex-wrap items-center gap-2">
          <span className="bg-[#FF3B30] text-white font-['Anton'] text-[11px] uppercase tracking-[0.25em] px-2.5 py-1 animate-pulse">
            New
          </span>
          <span className="border border-[#FF3B30]/60 text-[#FF3B30] font-['Outfit'] text-[10px] uppercase tracking-[0.25em] px-2.5 py-1">
            Tavern Style · Cracker Crust
          </span>
        </div>
        <h3 className="font-['Anton'] uppercase tracking-tight text-white leading-[0.9] text-4xl md:text-5xl mt-4">
          Tavern Cracker-Crust <span className="text-[#FF3B30]">Pizzas</span>
        </h3>
        <p className="font-['Outfit'] italic text-[#FF3B30] mt-2 text-sm md:text-base">
          Ultra thin. Ultra crispy. Perfect for bar snacking.
        </p>
        <div className="mt-5 flex flex-wrap items-center gap-3">
          {TAVERN_ITEMS.map((item) => {
            const itemSoldOut = soldOut(item.id);
            const disabled = !isOpen || itemSoldOut;
            return (
              <button
                key={item.id}
                onClick={() => !disabled && cart.add(item)}
                disabled={disabled}
                data-testid={`tavern-add-${item.id}`}
                className="inline-flex items-center gap-2 bg-[#FF3B30] text-white font-['Anton'] text-sm tracking-widest uppercase px-6 py-3 hover:bg-white hover:text-black transition-colors disabled:bg-zinc-700 disabled:text-zinc-400 disabled:cursor-not-allowed"
              >
                {!isOpen ? (
                  <>
                    <Lock className="w-4 h-4" /> Closed
                  </>
                ) : itemSoldOut ? (
                  <>
                    <Lock className="w-4 h-4" /> Sold Out
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4" />
                    {item.short} · $16
                  </>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const DessertBanner = ({ isOpen, soldOut }) => {
  const cart = useCart();
  const item = DESSERT_ITEM;
  const disabled = !isOpen || soldOut;
  return (
    <div
      data-testid="dessert-banner"
      className="relative mb-12 md:mb-16 overflow-hidden border-2 border-[#FF8C00] flex flex-col md:flex-row items-stretch"
      style={{
        background: "linear-gradient(100deg, #1c0f00 0%, #000 60%, #140600 100%)",
        boxShadow: "0 0 60px -15px rgba(255,140,0,0.45)",
      }}
    >
      <div className="relative w-full md:w-80 flex-shrink-0">
        <img
          src={item.image}
          alt={item.name}
          className="w-full h-56 md:h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-transparent to-black/60 hidden md:block" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent md:hidden" />
      </div>
      <div className="flex-1 p-6 md:p-8 flex flex-col justify-center relative">
        <div className="flex flex-wrap items-center gap-2">
          <span className="bg-[#FF8C00] text-black font-['Anton'] text-[11px] uppercase tracking-[0.25em] px-2.5 py-1 animate-pulse">
            New Dessert
          </span>
          <span className="border border-[#FF8C00]/60 text-[#FF8C00] font-['Outfit'] text-[10px] uppercase tracking-[0.25em] px-2.5 py-1">
            You didn't hear it from us
          </span>
        </div>
        <h3 className="font-['Anton'] uppercase tracking-tight text-white leading-[0.9] text-4xl md:text-5xl mt-4">
          Forbidden <span className="text-[#FF8C00]">AF</span> Fried Oreos
        </h3>
        <p className="font-['Outfit'] italic text-[#FF8C00] mt-2 text-sm md:text-base">
          {item.tagline}
        </p>
        <div className="mt-5 flex flex-wrap items-center gap-4">
          <span className="font-['Anton'] text-white text-3xl md:text-4xl tracking-wider">
            3 for <span className="text-[#FF8C00]">$6</span>
          </span>
          <button
            onClick={() => !disabled && cart.add(item)}
            disabled={disabled}
            data-testid="dessert-add-btn"
            className="inline-flex items-center gap-2 bg-[#FF8C00] text-black font-['Anton'] text-sm tracking-widest uppercase px-6 py-3 hover:bg-white transition-colors disabled:bg-zinc-700 disabled:text-zinc-400 disabled:cursor-not-allowed"
          >
            {!isOpen ? (
              <>
                <Lock className="w-4 h-4" /> Kitchen Closed
              </>
            ) : soldOut ? (
              <>
                <Lock className="w-4 h-4" /> Sold Out
              </>
            ) : (
              <>
                <Plus className="w-4 h-4" /> Add to Order · $6
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const MenuSection = () => {
  const [hero, ...baseRest] = MENU_ITEMS;
  const kitchen = useKitchenStatus();
  const availability = useMenuAvailability();
  const promos = usePromos();
  const isOpen = kitchen.is_open !== false;
  const soldOut = (id) => availability.availability[id] === false;
  const wingSpecialOn = promos.wing_special === true;
  const applyPromo = (item) =>
    wingSpecialOn && WING_IDS.includes(item.id)
      ? {
          ...item,
          price: 9,
          originalPrice: item.price,
          tags: ["$9 Wing Special", ...item.tags.filter((t) => t !== "New")],
        }
      : item;
  const activeSpecials = SPECIAL_ITEMS.filter(
    (s) => availability.availability[s.id] === true
  );
  const sundayOn = promos.sunday_menu === true;
  const pool = [hero, ...baseRest].filter(
    (i) =>
      (sundayOn || i.id !== TOP_ID) &&
      !SWEET_IDS.includes(i.id) &&
      (!sundayOn || SUNDAY_IDS.includes(i.id))
  );
  const [heroItem0, ...rest0] = pool;
  const heroItem = applyPromo(heroItem0);
  const sweetItems = [
    DESSERT_ITEM,
    ...(sundayOn
      ? []
      : MENU_ITEMS.filter((i) => SWEET_IDS.includes(i.id))),
  ];
  const rest = [...activeSpecials, ...rest0].map(applyPromo);

  return (
    <section
      id="menu"
      data-testid="menu-section"
      className="relative py-24 md:py-36 bg-black"
    >
      {/* Smoke texture overlay */}
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-[0.06] mix-blend-screen pointer-events-none"
        style={{
          backgroundImage: `url(${SMOKE_TEXTURE})`,
          backgroundSize: "cover",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        {/* Section header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16 md:mb-24">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-12 bg-[#FF3B30]" />
              <span className="font-['Outfit'] text-xs uppercase tracking-[0.4em] text-[#FF8C00]">
                The Menu
              </span>
            </div>
            <h2
              data-testid="menu-heading"
              className="font-['Anton'] text-5xl md:text-7xl tracking-tighter uppercase text-white leading-[0.9]"
            >
              {sundayOn ? pool.length + 1 : MENU_ITEMS.length + 1} dishes.
              <br />
              <span className="text-[#FF3B30]">All killer.</span> No filler.
            </h2>
          </div>
          <p className="max-w-sm font-['Outfit'] text-base text-zinc-400 leading-relaxed">
            {sundayOn ? (
              <>
                Today we're running a{" "}
                <span className="text-white font-semibold">limited menu</span> —
                smoked favorites only. Full lineup returns soon.
              </>
            ) : (
              <>
                Every plate is built around real wood smoke and our house pulled
                pork. <span className="text-white font-semibold">$16</span> wood-fired pizzas, <span className="text-white font-semibold">$14</span> Pork Crack, <span className="text-white font-semibold">$7</span> mini.
              </>
            )}
          </p>
        </div>

        {/* Closed banner */}
        {!isOpen && (
          <div
            data-testid="kitchen-closed-banner"
            className="mb-12 md:mb-16 bg-[#FF3B30]/10 border-2 border-[#FF3B30] px-6 md:px-10 py-6 md:py-8 flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-6"
          >
            <Lock className="w-8 h-8 text-[#FF3B30] flex-shrink-0" />
            <div className="flex-1">
              <h3 className="font-['Anton'] text-2xl md:text-3xl uppercase tracking-tight text-white leading-none">
                Kitchen's Closed
              </h3>
              <p className="font-['Outfit'] text-zinc-300 mt-2">
                {kitchen.message || "We're done for the night. Check back tomorrow."}
              </p>
            </div>
          </div>
        )}

        {/* Flapjack Chicken Bombs banner — very top */}
        {!sundayOn && (
          <BombsBanner isOpen={isOpen} soldOut={soldOut(BOMBS_ITEM.id)} />
        )}

        {/* Loaded Pork Fries banner */}
        {!sundayOn && (
          <FriesBanner isOpen={isOpen} soldOut={soldOut(TOP_ID)} />
        )}

        {/* Fried Fish Plate banner — permanent */}
        {!sundayOn && (
          <FishBanner isOpen={isOpen} soldOut={soldOut(FISH_ITEM.id)} />
        )}

        {/* Bento grid */}
        <div className="grid grid-cols-12 gap-5 md:gap-6">
          {/* Featured hero card */}
          <div className="col-span-12 lg:col-span-8 lg:row-span-3">
            <MenuCard item={heroItem} featured idx={0} isOpen={isOpen} soldOut={soldOut(heroItem0.id)} className="h-full" />
          </div>

          {/* Right column cards (3 stacked next to hero) */}
          {rest.slice(0, 3).map((item, i) => (
            <div key={item.id} className="col-span-12 sm:col-span-6 lg:col-span-4">
              <MenuCard item={item} idx={i + 1} isOpen={isOpen} soldOut={soldOut(item.id)} className="h-full" />
            </div>
          ))}

          {/* Bottom row — last card spans full width if odd count to avoid orphans */}
          {rest.slice(3).map((item, i, arr) => {
            const isLastOdd = i === arr.length - 1 && arr.length % 2 === 1;
            return (
              <div
                key={item.id}
                className={`col-span-12 sm:col-span-6 ${
                  isLastOdd ? "lg:col-span-12" : "lg:col-span-6"
                }`}
              >
                <MenuCard item={item} idx={i + 4} isOpen={isOpen} soldOut={soldOut(item.id)} className="h-full" />
              </div>
            );
          })}
        </div>

        {/* Sweet AF dessert section */}
        <div data-testid="sweet-af-section" className="mt-12 md:mt-16 mb-12 md:mb-16">
          <div className="flex flex-wrap items-center gap-3 md:gap-4 mb-6">
            <div
              className="hidden md:block bg-[#FF8C00]"
              style={{ height: 10, width: 110 }}
            />
            <h3 className="font-['Anton'] text-3xl md:text-5xl uppercase tracking-tight text-white leading-none">
              Sweet <span className="text-[#FF8C00]">AF</span>
            </h3>
            <span className="font-['Outfit'] text-[10px] md:text-xs uppercase tracking-[0.3em] text-[#FF8C00] border border-[#FF8C00]/50 px-2.5 py-1">
              Desserts
            </span>
            <div className="flex-1 min-w-[60px] bg-[#FF8C00]" style={{ height: 10 }} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {sweetItems.map((item, i) => (
              <MenuCard
                key={item.id}
                item={item}
                idx={i}
                isOpen={isOpen}
                soldOut={soldOut(item.id)}
                className="h-full"
              />
            ))}
          </div>
        </div>

        {/* Tavern pizzas banner (hidden on Sunday menu — no pizzas) */}
        {!sundayOn && <TavernBanner isOpen={isOpen} soldOut={soldOut} />}

        {/* Footnote */}
        <div
          data-testid="menu-footnote"
          className="mt-20 border-t border-white/10 pt-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 font-['Outfit'] text-sm text-zinc-500"
        >
          <p>
            All items served fresh from the smoker. Speared fries on every plate
            that warrants 'em.
          </p>
          <p className="font-['Anton'] tracking-widest uppercase text-zinc-300">
            Cash · Card · Venmo
          </p>
        </div>
      </div>
    </section>
  );
};

const StorySection = () => (
  <section
    id="story"
    data-testid="story-section"
    className="relative py-24 md:py-36 bg-[#0a0a0a] border-t border-white/5"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12 items-start">
      <div className="md:col-span-5">
        <div className="flex items-center gap-3 mb-6">
          <span className="h-px w-12 bg-[#FF3B30]" />
          <span className="font-['Outfit'] text-xs uppercase tracking-[0.4em] text-[#FF8C00]">
            The Story
          </span>
        </div>
        <h2 className="font-['Anton'] text-5xl md:text-7xl tracking-tighter uppercase text-white leading-[0.9]">
          Built in
          <br />
          <span className="text-[#FF3B30]">Smoke.</span>
        </h2>
      </div>
      <div className="md:col-span-7 space-y-6 font-['Outfit'] text-base md:text-lg text-zinc-400 leading-relaxed">
        <p>
          We don't do shortcuts. Pork shoulders go into a real wood smoker
          before sunrise and don't come out till the bark is black and the meat
          falls apart on its own.
        </p>
        <p>
          Our crusts hit a screaming oven. Our beef gets smoked, not just grilled.
          Our fries are spear-cut by hand. Everything here is loud, honest, and
          built to ruin your shirt.
        </p>
        <p className="text-white font-['Anton'] tracking-widest uppercase text-sm">
          — Pitmaster, Smokey AF
        </p>
      </div>
    </div>
  </section>
);

const Visit = () => (
  <section
    id="visit"
    data-testid="visit-section"
    className="relative py-24 md:py-32 bg-black border-t border-white/5"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="flex items-center gap-3 mb-10">
        <span className="h-px w-12 bg-[#FF3B30]" />
        <span className="font-['Outfit'] text-xs uppercase tracking-[0.4em] text-[#FF8C00]">
          Find Us
        </span>
      </div>
      <div className="grid md:grid-cols-2 gap-6">
        {[
          {
            icon: MapPin,
            label: "Where",
            value: "Southern Style · 648 N Broad St, Fremont, NE 68025",
            test: "visit-location",
            href: "https://www.google.com/maps/search/?api=1&query=Southern+Style+648+N+Broad+St+Fremont+NE+68025",
          },
          {
            icon: Instagram,
            label: "Follow",
            value: "@smokeyaf.bbq",
            test: "visit-social",
            href: "#",
          },
        ].map(({ icon: Icon, label, value, test, href }) => (
          <a
            key={label}
            href={href}
            target={href.startsWith("http") ? "_blank" : undefined}
            rel={href.startsWith("http") ? "noopener noreferrer" : undefined}
            data-testid={test}
            className="block bg-[#111] border border-white/5 p-8 hover:border-[#FF3B30]/50 transition-colors"
          >
            <Icon className="w-6 h-6 text-[#FF3B30]" />
            <div className="mt-6 font-['Outfit'] text-xs uppercase tracking-[0.3em] text-zinc-500">
              {label}
            </div>
            <div className="mt-2 font-['Anton'] text-2xl md:text-3xl uppercase tracking-tight text-white leading-tight">
              {value}
            </div>
          </a>
        ))}
      </div>
    </div>
  </section>
);

const Footer = () => (
  <footer
    data-testid="site-footer"
    className="bg-black border-t border-white/10 py-10"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col md:flex-row items-center justify-between gap-4">
      <div className="flex items-center gap-2">
        <Flame className="w-4 h-4 text-[#FF3B30]" />
        <span className="font-['Anton'] tracking-widest uppercase text-zinc-400 text-sm">
          Smokey AF Barbecue
        </span>
      </div>
      <p className="font-['Outfit'] text-xs uppercase tracking-[0.3em] text-zinc-600">
        © {new Date().getFullYear()} · Cooked Low. Served Loud.
      </p>
    </div>
  </footer>
);

export default function Menu() {
  const availability = useMenuAvailability();
  const promos = usePromos();
  const activeSpecials = SPECIAL_ITEMS.filter(
    (s) => availability.availability[s.id] === true
  );
  const bannerEntries = [
    ...activeSpecials,
    ...(promos.wing_special === true
      ? [
          {
            id: "garlic_parmesan_wings",
            name: "Wednesday Wing Special · All Wings",
            price: 9,
          },
        ]
      : []),
  ];
  return (
    <div className="bg-black min-h-screen text-white antialiased selection:bg-[#FF3B30] selection:text-white">
      <Navbar />
      <SpecialsBanner specials={bannerEntries} />
      <Hero />
      <MenuSection />
      <StorySection />
      <Visit />
      <Footer />
    </div>
  );
}
